import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../store/hooks";
import { RootState } from "../store";
import {
  initTeamSession,
  fetchTeamSession,
  updateTeamSessionState,
  fetchRtcToken,
  fetchTeamNotes,
  fetchTeamTasks,
  fetchTeamCheckins,
  updateTeamTimer,
  endTeamSession,
  createTeamCheckin,
  updateTeamCheckin,
  participantJoinSession,
  participantLeaveSession,
} from "../store/slices/teamFocusSessionSlice";
import type { TeamSessionState, ApiTeamFocusSession } from "../store/slices/teamFocusSession/types";
import { toast } from "react-toastify";
import { useTeamSessionTimer } from "./useTeamSessionTimer";
import { useTeamSessionSocket } from "./useTeamSessionSocket";

export interface UseTeamSessionHandlers {
  handleStartSession: () => void;
  handlePauseSession: () => void;
  handleResumeSession: () => void;
  confirmEndSession: () => void;
  handleCheckInAdd: (title: string, checkinTime: string) => void;
  handleCheckinUpdate: (checkinId: string, title: string, checkinTime: string) => void;
}

export interface UseTeamSessionReturn {
  sess: NonNullable<TeamSessionState>;
  loadingSession: boolean;
  fetchError: boolean;
  isSessionInvalid: boolean;
  currentUserId: string;
  handlers: UseTeamSessionHandlers;
  retry: () => void;
}

export function useTeamSession(teamId: string | undefined): UseTeamSessionReturn {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();

  const session = useSelector((state: RootState) =>
    teamId ? state.teamFocusSessions.sessions[teamId] : null,
  );
  const currentUser = useSelector((state: RootState) => state.user?.user);
  const currentUserId = currentUser?.id || "1";

  // Validate preloaded session passed via navigation state to prevent arbitrary data injection
  const navState = location.state as Record<string, unknown> | null;
  const rawPreloaded = navState?.teamSession as Record<string, unknown> | undefined;

  let preloaded: any | undefined;
  if (rawPreloaded && typeof rawPreloaded === 'object') {
    preloaded = {
      sessionId: rawPreloaded.id ? String(rawPreloaded.id) : undefined,
      roomId: rawPreloaded.roomId ? String(rawPreloaded.roomId) : undefined,
      hostId: rawPreloaded.hostId ? String(rawPreloaded.hostId) : undefined,
      projectId: rawPreloaded.projectId ? String(rawPreloaded.projectId) : undefined,
      status: rawPreloaded.status ? String(rawPreloaded.status) : undefined,
      goal: rawPreloaded.goal ? String(rawPreloaded.goal) : undefined,
      duration: typeof rawPreloaded.duration === 'number' ? rawPreloaded.duration : undefined,
      participants: Array.isArray(rawPreloaded.participants) ? rawPreloaded.participants : [],
      rtcToken: rawPreloaded.rtcToken ? String(rawPreloaded.rtcToken) : undefined,
    };
  }

  useTeamSessionTimer(teamId, session);
  useTeamSessionSocket(teamId);

  const hasInitialSession = !!session || !!(preloaded && preloaded.roomId === teamId);
  const [loadingSession, setLoadingSession] = useState(!hasInitialSession);
  const [fetchError, setFetchError] = useState(false);

  // Consolidate all fetch guards into a single ref mapped by data type -> teamId
  const fetchedRefs = useRef<Record<string, string>>({});

  // load or initialize session data
  useEffect(() => {
    if (!teamId) return;
    if (fetchedRefs.current.session === teamId) return;

    const isPreloadedValid = preloaded && preloaded.roomId === teamId;

    if (isPreloadedValid) {
      dispatch(initTeamSession({ teamId, initialData: preloaded }));
      fetchedRefs.current.session = teamId;
    } else if (!session || fetchedRefs.current.session !== teamId) {
      setLoadingSession(true);
      setFetchError(false);
      dispatch(fetchTeamSession(teamId))
        .unwrap()
        .then(() => {
          fetchedRefs.current.session = teamId;
        })
        .catch(() => {
          setFetchError(true);
        })
        .finally(() => setLoadingSession(false));
    }
  }, [teamId, preloaded, session, dispatch]);

  useEffect(() => {
    if (!teamId || fetchedRefs.current.rtcToken === teamId) return;
    dispatch(fetchRtcToken(teamId));
    fetchedRefs.current.rtcToken = teamId;
  }, [teamId, dispatch]);

  useEffect(() => {
    if (!teamId || fetchedRefs.current.notes === teamId) return;
    dispatch(fetchTeamNotes({ teamId }));
    fetchedRefs.current.notes = teamId;
  }, [teamId, dispatch]);

  useEffect(() => {
    if (!teamId || fetchedRefs.current.tasks === teamId) return;
    dispatch(fetchTeamTasks(teamId));
    fetchedRefs.current.tasks = teamId;
  }, [teamId, dispatch]);

  useEffect(() => {
    if (!teamId || fetchedRefs.current.checkins === teamId) return;
    dispatch(fetchTeamCheckins(teamId));
    fetchedRefs.current.checkins = teamId;
  }, [teamId, dispatch]);

  // Handle participant join/leave via LiveKit connection status
  useEffect(() => {
    if (!teamId || !session?.rtcToken) return;
    dispatch(participantJoinSession(teamId));

    return () => {
      dispatch(participantLeaveSession(teamId));
    };
  }, [teamId, session?.rtcToken, dispatch]);

  const hasSession =
    !!teamId && (session || (preloaded && preloaded.roomId === teamId));
  const isSessionInvalid = !!teamId && !hasSession && !loadingSession && !fetchError;

  // Capture when the last participant leaves automatically (e.g., closing tab)
  useEffect(() => {
    const handleUnload = () => {
      if (session?.teamMembers && session.teamMembers.length <= 1) {
        if (teamId) {
          const baseURL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api';
          navigator.sendBeacon(`${baseURL}/focus/team/${teamId}/end`);
        }
      }
    };
    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [teamId, session?.teamMembers]);

  // choose whichever data is available and merge with defaults so arrays never end up undefined
  const baseData = session || preloaded || {};
  const sess = {
    ...baseData,
    tasks: baseData.tasks || [],
    notes: baseData.notes || [],
    reminders: baseData.reminders || [],
    teamMembers: baseData.teamMembers || [],
    checkins: baseData.checkins || [],
    participants: baseData.participants || [],
    sessionId: baseData.sessionId || null,
    hostId: baseData.hostId || null,
    projectId: baseData.projectId || null,
    roomId: baseData.roomId || null,
    status: baseData.status || 'waiting',
    sessionStartedAt: baseData.sessionStartedAt || null,
  } as NonNullable<typeof session>;

  const handleStartSession = useCallback(() => {
    if (!teamId) return;
    dispatch(
      updateTeamSessionState({
        teamId,
        updates: {
          sessionStartedAt: new Date().toISOString(),
        },
      }),
    );
    dispatch(updateTeamTimer({ roomId: teamId, action: "start" }));
  }, [dispatch, teamId]);

  const handlePauseSession = useCallback(() => {
    if (!teamId) return;
    dispatch(updateTeamTimer({ roomId: teamId, action: "pause" }));
  }, [dispatch, teamId]);

  const handleResumeSession = useCallback(() => {
    if (!teamId) return;
    dispatch(
      updateTeamSessionState({
        teamId,
        updates: {
          sessionStartedAt: new Date().toISOString(),
        },
      }),
    );
    dispatch(updateTeamTimer({ roomId: teamId, action: "start" }));
  }, [dispatch, teamId]);

  const confirmEndSession = useCallback(() => {
    if (!teamId) return;
    dispatch(
      updateTeamSessionState({
        teamId,
        updates: { sessionStartedAt: null },
      }),
    );
    dispatch(endTeamSession(teamId));
    navigate("/dashboard");
  }, [dispatch, teamId, navigate]);

  const handleCheckInAdd = useCallback((title: string, checkinTime: string) => {
    if (teamId && title.trim() && checkinTime) {
      dispatch(createTeamCheckin({ teamId, data: { title: title.trim(), checkinTime, is_public: false } }))
        .unwrap()
        .then(() => toast.success("Check-in added successfully!"))
        .catch(() => toast.error("Failed to add check-in."));
    }
  }, [dispatch, teamId]);

  const handleCheckinUpdate = useCallback((checkinId: string, title: string, checkinTime: string) => {
    if (teamId && checkinId && title.trim() && checkinTime) {
      dispatch(updateTeamCheckin({ teamId, checkinId, data: { title: title.trim(), checkinTime } }))
        .unwrap()
        .then(() => toast.success("Check-in updated successfully!"))
        .catch(() => toast.error("Failed to update check-in."));
    }
  }, [dispatch, teamId]);

  const retry = useCallback(() => {
    if (!teamId) return;
    fetchedRefs.current.session = "";
    setFetchError(false);
    setLoadingSession(true);
    dispatch(fetchTeamSession(teamId))
      .unwrap()
      .then(() => { fetchedRefs.current.session = teamId; })
      .catch(() => setFetchError(true))
      .finally(() => setLoadingSession(false));
  }, [dispatch, teamId]);

  return {
    sess,
    loadingSession,
    fetchError,
    isSessionInvalid,
    currentUserId,
    handlers: {
      handleStartSession,
      handlePauseSession,
      handleResumeSession,
      confirmEndSession,
      handleCheckInAdd,
      handleCheckinUpdate,
    },
    retry,
  };
}
